import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stockApi } from '../../api/stock-api';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  locationTypeLabels,
  locationTypes,
  type LocationType,
  type WarehouseLocation,
} from '../../domain/stock';
import { siteSearchAliases } from '../../lib/site-label';
import { isUnknownOutcome, stockErrorMessage } from './error-message';
import type { StockReferences } from './use-stock-references';

/**
 * Creates a warehouse location, or edits one. A location's site and parent
 * never change; one that holds stock can't be deactivated.
 */
export function LocationDrawer({
  location,
  references,
  defaultSiteId,
  onClose,
}: {
  location?: WarehouseLocation | undefined;
  references: StockReferences;
  defaultSiteId?: string | undefined;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [siteId, setSiteId] = useState(location?.siteId ?? defaultSiteId ?? '');
  const [parentId, setParentId] = useState(location?.parentId ?? '');
  const [type, setType] = useState<LocationType>(
    (location?.type as LocationType | undefined) ?? 'SHELF',
  );
  const [code, setCode] = useState(location?.code ?? '');
  const [displayName, setDisplayName] = useState(location?.displayName ?? '');
  const [active, setActive] = useState(location?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const [unknown, setUnknown] = useState(false);

  const parentOptions = useMemo(
    () => [
      { value: '', label: 'None (top level)' },
      ...references.locations
        .filter((l) => l.siteId === siteId && l.active && l.id !== location?.id)
        .map((l) => ({ value: l.id, label: references.locationName(l.id), aliases: [l.code] })),
    ],
    [references, siteId, location?.id],
  );

  const save = useMutation({
    mutationFn: () =>
      location
        ? stockApi.updateWarehouseLocation(location.id, location.version, {
            code,
            displayName,
            type,
            active,
          })
        : stockApi.createWarehouseLocation({
            siteId,
            parentId: parentId || undefined,
            code,
            displayName,
            type,
            active,
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await queryClient.invalidateQueries({ queryKey: ['warehouse-locations'] });
      onClose();
    },
    onError: (failure: unknown) => {
      if (isUnknownOutcome(failure)) setUnknown(true);
      else setError(stockErrorMessage(failure, 'The location could not be saved.', 'location'));
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    if (!siteId || !code.trim()) {
      setError('A site and a code are required.');
      return;
    }
    save.mutate();
  }

  const title = location ? `Edit ${location.code}` : 'New location';
  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close location editor"
          >
            ×
          </button>
        </div>
        {error ? (
          <p role="alert" className="form-error">
            {error}
          </p>
        ) : null}
        {unknown ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['stock'] });
              onClose();
            }}
          />
        ) : null}
        {location ? (
          <p className="field-hint">
            {references.siteName(location.siteId)}
            {location.parentId ? ` › ${references.locationName(location.parentId)}` : ''}. Site and
            parent can’t change.
          </p>
        ) : (
          <>
            <label>
              Site
              <SearchableSelect
                ariaLabel="Location site"
                value={siteId}
                onChange={(value) => {
                  setSiteId(value);
                  setParentId('');
                }}
                options={references.sites.map((site) => ({
                  value: site.id,
                  label: references.siteName(site.id),
                  aliases: siteSearchAliases(site),
                }))}
                placeholder="Choose a site"
              />
            </label>
            <label>
              Inside
              <SearchableSelect
                ariaLabel="Parent location"
                value={parentId}
                onChange={setParentId}
                options={parentOptions}
                placeholder="None (top level)"
                disabled={!siteId}
              />
            </label>
          </>
        )}
        <div className="form-row">
          <label>
            Type
            <SearchableSelect
              ariaLabel="Location type"
              value={type}
              onChange={(value) => setType(value as LocationType)}
              options={locationTypes.map((candidate) => ({
                value: candidate,
                label: locationTypeLabels[candidate],
              }))}
              placeholder="Shelf"
            />
          </label>
          <label>
            Code
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="e.g. A-01"
            />
          </label>
        </div>
        <label>
          Name
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="e.g. Shelf A-01"
          />
        </label>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span>Active</span>
        </label>
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : location ? 'Save' : 'Create location'}
          </button>
        </div>
      </form>
    </div>
  );
}
